import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';

import { IProcessOrder } from '../../interfaces/IProcessOrder';
import { Provider, ProvidersCollection } from '../../interfaces/IProvider';
import { TSettings } from '../../services/appconfig/settings.model';
import { DeliveryProviderService } from '../../services/delivery-provider/delivery-provider.service';
import { OrderProcessingService } from '../../services/order-processing/order-processing.service';
import { SettingsService } from '../../services/settings/settings.service';
import {
  FRONT_PATHS,
  ORDER_ID_TYPE,
  REAR_PATHS,
  RETAIL_PATHS,
  VIEW_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';
import { DeliveryProviderDialogComponent } from '../delivery-provider-dialog/delivery-provider-dialog.component';

@Component({
  selector: 'frodo-delivery-provider',
  templateUrl: './delivery-provider.component.html',
  styleUrls: ['./delivery-provider.component.scss'],
})
export class DeliveryProviderComponent implements OnInit, OnDestroy {
  logo: string;
  title = 'common.deliveryProvider.title';
  button = 'common.deliveryProvider.button';
  providers: Provider[];
  hdSettings$: Observable<Partial<TSettings>>;
  subscription: Subscription = new Subscription();
  timeout: number;
  selectedProvider: Provider;
  cardType: string;
  flow: string;
  inactivityTimeout;
  dialogInstance;
  processOrder: IProcessOrder;
  isLoading: boolean;
  guidedLoadFailForward: boolean;
  isContinueEnabled = false;

  constructor(
    private deliveryProviderService: DeliveryProviderService,
    private sanitizer: DomSanitizer,
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private utils: Utils,
    private orderProcessingService: OrderProcessingService
  ) {
    this.hdSettings$ = this.settingsService.getSettings;
    this.processOrder = this.orderProcessingService.orderToProcess;
  }

  ngOnInit(): void {
    this.cardType = this.processOrder.type;
    this.getProviders();
    this.getSettings();
    this.inactivityTimer();
    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );
  }

  getProviders(): void {
    this.deliveryProviderService.getProviders().subscribe((providers: ProvidersCollection) => {
      this.providers = Object.entries(providers).map(([key, value]) => ({
        name: key,
        label: value.label,
        logo: value.logo,
        selected: false,
        safeLogo: this.sanitizer.bypassSecurityTrustUrl(value.logo),
      }));
    });
  }

  onProviderSelected(provider: Provider): void {
    if (provider.selected) {
      provider.selected = false;
      this.selectedProvider = undefined;
      this.isContinueEnabled = false;
      return;
    }
    provider.selected = true;
    if (this.selectedProvider) this.selectedProvider.selected = false;
    this.selectedProvider = provider;
    this.isContinueEnabled = true;
  }

  onContinue(): void {
    this.processOrder.deliveryProvider = this.selectedProvider?.name;
    this.orderProcessingService.processOrder(this.processOrder);
    clearTimeout(this.inactivityTimeout);
  }

  getSettings(): void {
    this.subscription.add(
      this.hdSettings$.subscribe((settings: TSettings) => {
        this.logo = settings.apps.frodo.style.logo;
        this.timeout = settings.apps.frodo.timeouts.deliveryProvider;
        this.guidedLoadFailForward = settings.device.guidedLoadFailForward;
      })
    );
  }

  inactivityTimer(): void {
    this.inactivityTimeout = setTimeout(() => {
      if (!this.selectedProvider) {
        this.openInactivityDialog();
        return;
      }
      if (this.guidedLoadFailForward) {
        this.onContinue();
      } else {
        this.onGoBack();
      }
    }, this.timeout);
  }

  openInactivityDialog(): void {
    this.dialogInstance = this.dialog.open(DeliveryProviderDialogComponent, {
      disableClose: true,
      backdropClass: 'modal-blurred-background',
    });
    this.dialogInstance.afterClosed().subscribe((isRedirect: boolean) => {
      if (isRedirect) {
        if (this.inactivityTimeout) {
          clearTimeout(this.inactivityTimeout);
        }
      } else {
        this.restartTimeout();
      }
    });
  }

  restartTimeout(): void {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    this.inactivityTimer();
  }

  onGoBack(): void {
    const frodoContext = this.processOrder.frodoContext;
    const pathsByContext = {
      [VIEW_TYPES.REAR_LOADING]:
        this.cardType === ORDER_ID_TYPE
          ? `/${REAR_PATHS.CREATE_ORDER}`
          : `/${REAR_PATHS.CREATE_ORDER_BY_PHONE}`,
      [VIEW_TYPES.FRONT_LOADING]: `/${FRONT_PATHS.CREATE_ORDER}`,
      [VIEW_TYPES.RETAIL]: `/${RETAIL_PATHS.CREATE_ORDER}`,
    };
    const path = pathsByContext[frodoContext];
    const url = `${frodoContext}${path}`;
    const params = {
      type: this.cardType,
      flow: this.processOrder.flow,
    };
    this.utils.redirect(url, params);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.inactivityTimeout) clearTimeout(this.inactivityTimeout);
  }
}
